/**
 * useMercureReconnect — Handles SSE reconnection when user permissions change.
 *
 * When a permission update is detected via the Mercure subscription,
 * this hook:
 * 1. Fetches a fresh Mercure subscription JWT from the backend
 * 2. Stores the updated token
 * 3. Triggers the Mercure client to reconnect with new credentials
 *
 * This ensures the SSE connection stays authorized after role/permission
 * changes that may affect which Mercure topics the user can subscribe to.
 */

import { useAuthStore } from "@lib/auth/authStore";
import { API_ENDPOINTS } from "@lib/config/constants";
import { usePermissionStore } from "@lib/stores/permissions.store";
import { getMercureClient } from "@lib/utils/mercure";
import { useEffect, useRef } from "react";

const LOG_PREFIX = "[MercureReconnect]";

/**
 * Fetch a fresh Mercure subscription token from the backend.
 * The backend generates a short-lived JWT encoding the user's
 * current Mercure topic authorization.
 */
async function fetchMercureToken(): Promise<string | null> {
  try {
    const { api } = await import("@lib/api/client");
    const response = await api.get<{ token: string }>(API_ENDPOINTS.MERCURE_TOKEN);
    return response.data.token ?? null;
  } catch (error) {
    console.error(`${LOG_PREFIX} Failed to fetch Mercure token:`, error);
    return null;
  }
}

/**
 * Hook that monitors permission changes and triggers Mercure SSE reconnection.
 *
 * Mount this hook inside `AppMercureSubscriber` (or any component that
 * has access to the Mercure context). It subscribes to Zustand permission
 * store changes and re-establishes the SSE connection when needed.
 *
 * @example
 * ```tsx
 * function AppMercureSubscriber({ children }) {
 *   useMercureReconnect();
 *   return children;
 * }
 * ```
 */
export function useMercureReconnect(): void {
  const previousPermissionsHash = useRef<string>("");
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Subscribe to permission store changes (Zustand selector)
    const unsubscribe = usePermissionStore.subscribe((state) => {
      // Build a lightweight hash of permission-relevant state
      const hash = [
        state.permissions.join(","),
        state.hasWildcard ? "1" : "0",
        state.context.filiale_code,
        state.context.accessible_filiales.join(","),
      ].join("|");

      // Skip the initial mount — we only react to *changes*
      if (isInitialMount.current) {
        isInitialMount.current = false;
        previousPermissionsHash.current = hash;
        return;
      }

      // No change → no-op
      if (hash === previousPermissionsHash.current) return;
      previousPermissionsHash.current = hash;

      // Permissions changed → refresh Mercure token and reconnect
      handlePermissionChange();
    });

    return unsubscribe;
  }, []);
}

/**
 * Core reconnection logic: fetch fresh token, update store, reconnect SSE.
 */
async function handlePermissionChange(): Promise<void> {
  if (import.meta.env?.DEV) {
    console.log(`${LOG_PREFIX} Permission change detected, refreshing Mercure connection...`);
  }

  // 1. Fetch fresh Mercure subscription JWT
  const newToken = await fetchMercureToken();

  if (newToken) {
    // 2. Store the token (accessible to MercureClient if needed)
    useAuthStore.getState().login(newToken);

    if (import.meta.env?.DEV) {
      console.log(`${LOG_PREFIX} New Mercure token stored`);
    }
  }

  // 3. Trigger SSE reconnection — MercureClient.rotateConnection()
  //    will close the current EventSource and open a new one
  //    with updated topic authorization.
  try {
    const client = getMercureClient();
    if (client && typeof client.rotateConnection === "function") {
      client.rotateConnection();
      if (import.meta.env?.DEV) {
        console.log(`${LOG_PREFIX} SSE reconnection triggered`);
      }
    } else if (import.meta.env?.DEV) {
      console.warn(
        `${LOG_PREFIX} MercureClient.rotateConnection not available — topics will update on next connect cycle`,
      );
    }
  } catch (error) {
    console.error(`${LOG_PREFIX} Failed to trigger reconnection:`, error);
  }
}

// Re-export for convenience
export default useMercureReconnect;
